<template>
  <div :class="['w-full flex flex-col outline-none']">
    <div
      tabindex="0"
      :class="[
        'rounded relative bg-white focus:outline-none border border-truegray-300',
        height ? `h-${height}` : '',
      ]"
    >
      <!-- Label -->
      <h6
        class="absolute transform duration-100 ease-linear px-1 left-3 top-1 z-20 text-truegray-700"
      >
        {{ label }}
        <span v-if="required" class="italic">required</span>
      </h6>
      <!-- /Label -->

      <!-- Placeholder -->
      <button
        type="button"
        :class="[
          `flex-grow px-4 mr-0 w-full outline-none focus:outline-none bg-transparent font-primary text-black placeholder-truegray-300`,
          focused ? 'rounded-b-none' : '',
          height ? `h-${height}` : '',
        ]"
        @click="focused = !focused"
      >
        <span
          :class="[
            'block truncate text-left mt-2 mr-6 text-truegray-300',
            !focused ? 'text-black' : 'text-truegray-300',
          ]"
        >
          {{ placeholderText }}
        </span>
      </button>
      <!-- /Placeholder -->

      <!-- Dropdown icon -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        :class="[
          `w-5 absolute top-0 flex right-2 transform transition-all duration-300 ease-in-out pointer-events-none`,
          { 'rotate-180': focused },
          height ? `h-${height}` : '',
        ]"
        @click="focused = !focused"
      >
        <path
          fill-rule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clip-rule="evenodd"
        />
      </svg>
      <!-- /Dropdown icon -->

      <!-- Options -->
      <div
        :class="[
          'flex no-scrollbar transform ease-in-out duration-300 shadow-md w-full absolute left-0 z-50 bg-white text-base sm:text-sm overflow-auto focus:outline-none rounded-b-lg rounded-b-md',
          focused ? 'top-12 opacity-1' : 'top-0 opacity-0 invisible',
        ]"
      >
        <div
          :class="[
            'flex-grow max-h-56',
            checkPositiveInteger(selectedListTypeId)
              ? 'flex-shrink-0'
              : 'w-1/2',
          ]"
        >
          <ul class="flex flex-col list-none flex-wrap py-0">
            <li v-for="listType in listTypes" :key="listType.id">
              <button
                class="flex justify-start items-center w-full px-5 py-3"
                :class="[
                  {
                    'bg-gray-100': isListTypeActive(listType.id),
                    'border-0 grayscale opacity-50': false,
                    'opacity-25 cursor-not-allowed': listType.disabled,
                    'hidden': listType.hidden(),
                  },
                ]"
                :disabled="listType.disabled"
                @click="selectListType(listType.id)"
              >
                <div class="flex items-center space-x-2">
                  <img
                    class="h-4 flex-shrink-0"
                    :class="
                      checkPositiveInteger(selectedListTypeId) ? '' : 'mr-2'
                    "
                    :src="listType.img"
                    alt="logo"
                  />
                  <span
                    v-if="!checkPositiveInteger(selectedListTypeId)"
                    class="capitalize"
                  >
                    {{ listType.text }}
                  </span>
                  <ProTag
                    v-if="
                      !checkPositiveInteger(selectedListTypeId) &&
                      listType.isPro
                    "
                    :config="proTagConfig"
                  />
                </div>
                <div
                  v-if="!!getListTypeSelectedCount(listType.id)"
                  class="text-white bg-primary rounded-full px-1 ml-2 text-xs"
                >
                  {{ getListTypeSelectedCount(listType.id) }}
                </div>
              </button>
            </li>
          </ul>
        </div>
        <div
          :class="
            checkPositiveInteger(selectedListTypeId) ? 'w-full' : 'invisible'
          "
          class="max-h-56 overflow-auto"
        >
          <ul>
            <li
              v-if="options.length > 0"
              :class="[
                'w-full cursor-pointer relative flex items-center px-4 border-b',
                height ? `h-${height}` : '',
              ]"
              @click="toggleSelectAll()"
            >
              <div class="flex space-x-2 items-center w-full justify-start">
                <div
                  :class="[
                    'h-3 w-3 rounded-full border-3 border-primary flex-shrink-0',
                    { 'bg-primary': allOptionsSelected },
                  ]"
                />
                <span class="block truncate text-truegray-500 text-left"
                  >Select all</span
                >
              </div>
            </li>
            <li
              v-for="opt in options"
              :key="opt.value"
              :disabled="opt.subscriberCount === 0"
              :class="[
                'w-full cursor-pointer relative flex items-center px-4',
                height ? `h-${height}` : '',
                opt.subscriberCount > 0 ? '' : 'cursor-not-allowed opacity-60',
              ]"
              role="option"
              @click="toggleListSelection(opt)"
            >
              <div class="flex space-x-2 items-center w-full justify-start">
                <div
                  :class="[
                    'h-3 w-3 rounded-full border-3 border-primary flex-shrink-0',
                    { 'bg-primary': isListSelected(opt.value) },
                  ]"
                />
                <span
                  class="block truncate text-truegray-500 text-left capitalize"
                  >{{ opt.text }}</span
                >
                <div
                  class="border border-primary px-1 text-[8px] text-primary rounded h-4 mb-auto flex items-center justify-center"
                >
                  {{ opt.subscriberCount }}
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Options: multiselect -->
    <div class="flex flex-wrap space-x-2 mt-2 w-full">
      <div v-for="opt in selectedOptions" :key="opt.value">
        <div v-if="isListSelected(opt.value)">
          <button
            type="button"
            class="mr-1 mb-1 rounded-full w-auto text-sm relative outline-none focus:outline-none border-0 flex flex-grow-0 justify-start items-stretch text-gray-500"
            :class="{ 'pointer-events-none': opt.mandatory }"
            :disabled="opt.mandatory"
            @click="toggleListSelection(opt)"
          >
            <span
              :class="opt.mandatory ? 'rounded-full' : 'rounded-l-full'"
              class="bg-primary text-white px-2 text-xs py-1 capitalize"
              >{{ opt.text }}</span
            >
            <div
              v-if="!opt.mandatory"
              :style="{ marginLeft: '1px' }"
              class="bg-primary text-white flex items-center justify-center rounded-r-full px-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-3 w-3 relative"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </button>
        </div>
      </div>
    </div>
    <!-- /Options: multiselect -->
  </div>
</template>

<script>
import { EventBus } from '../../../../../js/plugins/event-bus.js'
import { checkPositiveInteger } from '../../../../../js/util/helper.js'
import { mapGetters } from 'vuex'
import ProTag from '../../components/ProTag.vue'

export default {
  components: {
    ProTag,
  },

  props: {
    // data
    required: {
      type: Boolean,
      default: false,
    },

    label: {
      type: String,
      default: '',
    },

    placeholderDefault: {
      type: String,
      default: '',
    },

    responseSelectionId: {
      type: String,
      required: true,
    },

    // styling
    height: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      focused: false,
      selectedOptions: [],
      selectedListTypeId: null,
      proTagConfig: {
        small: true,
      },
    }
  },

  computed: {
    ...mapGetters({
      getMailListInputOptions: 'careerwise/getMailListInputOptions',
      getMailTypesConfig: 'careerwise/getMailTypesConfig',
      hasHail: 'careerwise/hasHail',
      hasSMS: 'careerwise/hasSMS',
    }),

    listTypes() {
      const hasHail = this.hasHail
      const hasSMS = this.hasSMS
      const optionsMap = {
        careerwise: {
          options: [],
          hidden: () => false,
        },

        hail: {
          options: [],
          hidden: () => !hasHail,
        },

        sms: {
          options: [],
          hidden: () => !hasHail || !hasSMS,
        },
      }

      return this.getMailTypesConfig(optionsMap)
    },

    /**
     * @returns {boolean} are all current options selected?
     */
    allOptionsSelected() {
      const a = this.optionsIds
      const b = this.selectedOptionsIds
      return a.every((id) => b.includes(id))
    },

    /**
     * @returns {array} array of current options
     */
    options() {
      return (
        this.listTypes.find((opt) => opt.id === this.selectedListTypeId)
          ?.options || []
      )
    },

    /**
     * @returns {array} array of current options' Hail ids
     */
    optionsIds() {
      return this.options.map((opt) => opt.value)
    },

    /**
     * @returns {array} array of selected options' Hail ids
     */
    selectedOptionsIds() {
      return this.selectedOptions.map((opt) => opt.value)
    },

    /**
     * @returns {num} number of total users being emailed
     */
    selectedOptionsSubscriberCount() {
      return this.selectedOptions.reduce(
        (total, item) => total + item.subscriberCount,
        0,
      )
    },

    /**
     * Selected option text, if it exists;
     * else default option passed in props
     */
    placeholderText() {
      return this.placeholderDefault
    },
  },

  watch: {
    /**
     * Reset the selected list type
     */
    focused: {
      handler: function () {
        this.$set(this, 'selectedListTypeId', null)
      },
    },

    /**
     * When options are changed, emit the change
     * back to the parent to update component/store
     */
    selectedOptions: {
      handler: function () {
        if (this.responseSelectionId) {
          const options = {
            selectedIds: this.selectedOptionsIds,
            subscriberCount: this.selectedOptionsSubscriberCount,
          }
          EventBus.$emit(this.responseSelectionId, options)
        }
      },

      deep: true,
    },
  },

  mounted() {
    // get/set lists for each list type
    this.listTypes.forEach((opt, i) => {
      const payload = {
        type: opt.text,
        byHailId: true,
      }
      const options = this.getMailListInputOptions(payload)
      this.$set(this.listTypes[i], 'options', options)
    })

    EventBus.$on('resetNewsletterSelectedMailLists', () => {
      this.$set(this, 'selectedOptions', [])
    })
  },

  methods: {
    checkPositiveInteger,

    selectListType(id) {
      this.$set(this, 'selectedListTypeId', id)
    },

    isListTypeActive(id) {
      return this.selectedListTypeId === id
    },

    getSelectedListIndex(id) {
      return this.selectedOptionsIds.indexOf(id)
    },

    isListSelected(id) {
      return this.getSelectedListIndex(id) > -1
    },

    getListTypeSelectedCount(listTypeId) {
      return this.listTypes
        .find((type) => type.id === listTypeId)
        .options.filter((opt) => this.isListSelected(opt.value))?.length
    },

    toggleSelectAll() {
      const toggledTo = !this.allOptionsSelected

      this.options.forEach((opt) => {
        if (!toggledTo) {
          // remove all from selected options
          const index = this.getSelectedListIndex(opt.value)
          if (index > -1) {
            // unselect option if already selected
            this.selectedOptions.splice(index, 1)
          }
        } else {
          // add all to selected options
          if (!this.isListSelected(opt.value) && opt.subscriberCount > 0) {
            this.selectedOptions.push(opt)
          }
        }
      })
    },

    toggleListSelection(opt) {
      const index = this.getSelectedListIndex(opt.value)
      if (opt.subscriberCount === 0) {
        return
      }
      if (index > -1) {
        // unselect this option
        this.selectedOptions.splice(index, 1)
      } else {
        // select this option
        this.selectedOptions.push(opt)
      }
    },
  },
}
</script>
